import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, GraduationCap, Building2, Wallet, Briefcase, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { SectionHeading, Reveal, Eyebrow } from "../components/common";
import { UniversityCard } from "../components/cards";
import { DESTINATIONS, UNIVERSITIES } from "../mock";

export default function DestinationDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const d = DESTINATIONS.find((x) => x.slug === slug);

  if (!d) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h2 className="font-display text-3xl font-bold text-[#012a1e]">Destination not found</h2>
        <Link to="/destinations"><Button className="mt-6 bg-[#004030] text-white rounded-full px-6">Back to destinations</Button></Link>
      </div>
    );
  }

  const unis = UNIVERSITIES.filter((u) => u.country === d.name);
  const facts = [
    { icon: Building2, label: "Institutions", value: `${d.universities.toLocaleString()}+` },
    { icon: GraduationCap, label: "Courses", value: `${d.courses.toLocaleString()}+` },
    { icon: Wallet, label: "Avg. Tuition / year", value: d.avgTuition },
    { icon: Briefcase, label: "Work Rights", value: d.workRights },
  ];

  return (
    <>
      <section className="relative bg-[#004030] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={d.image} alt={d.name} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#004030] via-[#004030]/90 to-[#004030]/50" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <span className="text-5xl">{d.flag}</span>
            <Eyebrow light>Study in {d.city}</Eyebrow>
          </div>
          <h1 className="font-display font-bold text-white text-4xl md:text-6xl mt-4">Study in {d.name}</h1>
          <p className="mt-5 text-white/75 text-lg max-w-2xl leading-relaxed">{d.intro}</p>
          <Link to="/contact"><Button className="mt-8 bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full h-12 px-7 font-bold">Get free counselling <ArrowRight size={17} className="ml-1" /></Button></Link>
        </div>
      </section>

      {/* Facts */}
      <section className="bg-white -mt-12 relative z-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 bg-white rounded-2xl shadow-xl border border-slate-100 p-6 md:p-8">
            {facts.map((f) => (
              <div key={f.label} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#f0f6f3] grid place-items-center text-[#004030] shrink-0">
                  <f.icon size={22} />
                </div>
                <div>
                  <p className="text-[#012a1e] font-bold text-lg leading-tight">{f.value}</p>
                  <p className="text-slate-500 text-xs">{f.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why study */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <img src={d.image} alt={d.name} className="rounded-2xl h-80 w-full object-cover shadow-lg" />
          <div>
            <SectionHeading center={false} eyebrow="Why this destination" title={`What makes ${d.name} special`} />
            <ul className="mt-6 space-y-3">
              {[
                `Globally recognised degrees valued by employers worldwide`,
                `Clear post-study work pathways: ${d.workRights}`,
                `Vibrant, safe and welcoming environment for international students`,
                `Scholarships and financial aid available with GDIH support`,
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-600">
                  <CheckCircle2 size={20} className="text-[#c9a227] shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Universities in country */}
      {unis.length > 0 && (
        <section className="section-pad bg-[#f4f8f6]">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeading eyebrow="Top Choices" title={`Universities in ${d.name}`} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
              {unis.map((u, i) => <Reveal key={u.slug} delay={i * 70}><UniversityCard u={u} /></Reveal>)}
            </div>
          </div>
        </section>
      )}
    </>
  );
}